import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from '@/components/ui/alert-dialog';
import type { FinalDecision } from '@/types';
import type { DecisionType } from './ReviewDecisionForm';

interface ConfirmSubmitDialogProps {
  decision: FinalDecision | null;
  decisionType: DecisionType | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Irreversibility confirmation modal for review submission.
 * FR-077: clearly states the contract will be activated, rejected, or escalated.
 * WCAG: uses AlertDialogContent (Radix Dialog.Content) which provides aria-modal
 *       and focus trap. "Cancel" receives autoFocus — prevents accidental confirmation.
 */
export function ConfirmSubmitDialog({
  decision,
  decisionType,
  onConfirm,
  onCancel,
}: ConfirmSubmitDialogProps) {
  const outcomeLabel =
    decision === 'APPROVED' ? 'activated' :
    decision === 'REJECTED' ? 'rejected' :
    'escalated for senior review';

  return (
    <AlertDialog open onOpenChange={(open) => { if (!open) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm Review Decision</AlertDialogTitle>
          <AlertDialogDescription>
            You are about to record a final decision of{' '}
            <strong className="text-foreground">{decision ?? decisionType}</strong>.
            This action is irreversible — the contract will be{' '}
            <strong className="text-foreground">{outcomeLabel}</strong> once submitted.
            Per FR-077: every gate outcome requires explicit human approval before activation.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {/* Cancel receives autoFocus — prevents accidental destructive confirmation */}
          <Button variant="outline" onClick={onCancel} autoFocus>
            Cancel
          </Button>
          <Button onClick={onConfirm}>
            Confirm Decision
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
